import { storageKey } from "@/site";

const RENEW_PATH = "/auth/reader-renew";
const RENEW_COMMIT_PATH = "/auth/reader-renew/commit";

// Per tab: the iat of the token the broker last refused for good. Every
// page load would otherwise ask the broker, and Convex, the same question.
export const RENEW_REFUSED_KEY = storageKey("renewRefused.v1");

type Fetch = (input: string, init: RequestInit) => Promise<Response>;
type Store = Pick<Storage, "getItem" | "setItem">;

const tabStorage = (): Store | null => {
  try {
    return sessionStorage;
  } catch {
    return null;
  }
};

function refusedIat(storage: Store | null): number | undefined {
  const stored = Number(storage?.getItem(RENEW_REFUSED_KEY) ?? Number.NaN);
  return Number.isFinite(stored) ? stored : undefined;
}

async function rememberRefusal(
  refused: Response,
  storage: Store | null,
): Promise<void> {
  const body: unknown = await refused.json();
  if (typeof body !== "object" || body === null || !("refusedIat" in body))
    return;
  if (typeof body.refusedIat === "number")
    storage?.setItem(RENEW_REFUSED_KEY, String(body.refusedIat));
}

/**
 * Asks the Worker to slide this device's reader_session. It renews only a
 * reader_session over a day old, so most visits answer 204 at once; a
 * renewed token is committed in a second request, which writes the cookie
 * only if nothing replaced it meanwhile.
 */
export async function renewReaderSession(
  fetchImpl: Fetch = (input, init) => fetch(input, init),
  storage: Store | null = tabStorage(),
): Promise<void> {
  try {
    const renewed = await fetchImpl(RENEW_PATH, {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ refusedIat: refusedIat(storage) }),
    });
    if (renewed.status === 409) {
      await rememberRefusal(renewed, storage);
      return;
    }
    if (renewed.status !== 200) return;
    const body: unknown = await renewed.json();
    if (typeof body !== "object" || body === null || !("token" in body)) return;
    await fetchImpl(RENEW_COMMIT_PATH, {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token: body.token }),
    });
  } catch {
    // The current session stands until its own expiry; the next visit asks again.
  }
}
